import { Modal, Text, Button, Group, Stack } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { TemplateListItem } from "../../types/template";

interface TemplateConfirmDialogProperties {
  /**
   * Whether dialog is open
   */
  opened: boolean;

  /**
   * Callback when dialog is closed
   */
  onClose: () => void;

  /**
   * Callback when delete is confirmed
   */
  onConfirm: () => void;

  /**
   * Template being deleted (for displaying name)
   */
  template: TemplateListItem | null;

  /**
   * Whether deletion is in progress
   */
  isDeleting?: boolean;
}

/**
 * Confirmation dialog for template deletion
 *
 * Features:
 * - Shows template name being deleted
 * - Requires explicit confirmation
 * - Loading state during deletion
 * @param root0
 * @param root0.opened
 * @param root0.onClose
 * @param root0.onConfirm
 * @param root0.template
 * @param root0.isDeleting
 * @example
 * ```tsx
 * <TemplateConfirmDialog
 *   opened={deleteDialogOpened}
 *   onClose={() => setDeleteDialogOpened(false)}
 *   onConfirm={() => handleDelete(selectedTemplate)}
 *   template={selectedTemplate}
 *   isDeleting={isDeleting}
 * />
 * ```
 */
export function TemplateConfirmDialog({
  opened,
  onClose,
  onConfirm,
  template,
  isDeleting = false
}: TemplateConfirmDialogProperties) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Confirmar Exclusão"
      centered
      closeOnClickOutside={!isDeleting}
      closeOnEscape={!isDeleting}
    >
      <Stack gap="md">
        {/* Warning Icon */}
        <Group gap="sm">
          <IconAlertTriangle size={24} color="var(--mantine-color-red-6)" />
          <Text size="sm" fw={500}>
            Tem certeza que deseja apagar este template?
          </Text>
        </Group>

        {/* Template Info */}
        {template && (
          <Stack gap="xs">
            <Text size="sm">
              <strong>Nome:</strong> {template.nome}
            </Text>
            <Text size="sm" c="dimmed">
              {template.descricao}
            </Text>
          </Stack>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button color="red" onClick={onConfirm} loading={isDeleting}>
            Apagar Template
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
